import React from 'react';
import { 
  FaUserFriends, FaTimesCircle, FaCheckCircle, FaBan, 
  FaExclamationCircle, FaExclamationTriangle, FaUser, FaUserPlus, FaUserSlash 
} from 'react-icons/fa';
import { query, collection, where, limit, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';
import ReportIncident from "./ReportIncident";

const LogCardItem = React.memo(({ 
  log, 
  formatDisplayValue, 
  formatScanTimeOnly, 
  handleNoShow, 
  handleVerifyEntry, 
  handleOpenBanModal, 
  setSelectedLog, 
  setShowDetailModal, 
  setLastMemberEmail, 
  setLastMemberName, 
  fetchGuestsForMember, 
  setShowGuestModal, 
  db 
}) => {
  const isMember = log.entryType === 'Member' || !log.entryType;
  const hasViolation = log.isExpired || log.isFlagged || log.hasIncident || log.isBanned;
  const isBypassed = log.status === 'bypassed';
  const isRemovedOrNoShow = log.status === 'removed' || log.status === 'cancelled' || log.noshow === true || log.remove === true;
  const lastScanTime = log.lastScannedAt || log.scannedAt;
  const showFooterBar = !isRemovedOrNoShow && (!hasViolation || isBypassed);

  // Clean format helper for Sponsor display: prefers sponsorName over email
  const displaySponsor = React.useMemo(() => {
    const rawSponsor = log.sponsorName || log.signedInBy;
    if (!rawSponsor) return '';
    if (rawSponsor.includes('@')) {
      const namePart = rawSponsor.split('@')[0].replace(/[._-]/g, ' ');
      return formatDisplayValue(namePart);
    }
    return formatDisplayValue(rawSponsor);
  }, [log.sponsorName, log.signedInBy, formatDisplayValue]);

  return (
    <div
      onClick={() => {
        if (!isMember) return;
        setSelectedLog({
          ...log,
          entryType: log.entryType || 'Member',
          status: log.status || 'active',
          isExpired: log.isExpired || false,
          isFlagged: log.isFlagged || false,
          hasIncident: log.hasIncident || false,
          isBanned: log.isBanned || false
        });
        setShowDetailModal(true);
      }}
      className={`qr-log-card ${isRemovedOrNoShow ? 'opacity-50' : ''}`}
      style={{ cursor: isMember ? 'pointer' : 'default', transform: isMember ? undefined : 'none' }}
    >
      <div className="qr-card-content flex-column w-100">
        <div className="d-flex justify-content-between align-items-start w-100 mb-2">
          <div className="qr-card-main-info me-2">
            <div className="qr-card-name fw-bold">
              {formatDisplayValue(log.fullName)}
              {log.isBanned ? <FaBan className="ms-2 text-danger" title="Banned" /> : log.isFlagged ? <FaBan className="ms-2 text-warning" title="Flagged" /> : log.hasIncident ? <FaExclamationTriangle className="ms-2 text-danger" title="Incident" /> : log.isExpired && <FaExclamationCircle className="ms-2 text-danger" title="Expired" />}
              <span className="ms-2">
                {log.isVerified ? (
                  <FaCheckCircle className="text-success" title={`Verified by ${log.verifiedBy || 'Staff'}`} size={14} />
                ) : (
                  <FaTimesCircle className="text-danger" title={isMember ? "Unverified Member" : "Unverified Guest"} size={14} />
                )}
              </span>
            </div>
            <div className="qr-card-meta">
              <span className={`qr-badge ps-0 ms-0 ${log.entryType === 'Guest' ? 'qr-badge-guest' : (hasViolation && !isBypassed ? 'qr-badge-expired' : 'qr-badge-member')}`}>
                {log.entryType === 'Guest' ? <FaUserFriends size={10} className="me-1" /> : <FaUser size={10} className="me-1" />}
                {log.entryType || 'Member'}
              </span>
              {log.entryType === 'Guest' && displaySponsor && (
                <span className="ms-2 qr-subtext-small opacity-75 text-white">
                  Sponsor: <strong className="text-white">{displaySponsor}</strong>
                </span>
              )}
              {log.eventListName && (
                <span className="ms-2 badge bg-primary text-white font-10" style={{ fontSize: '0.65rem' }}>
                  Event: {log.eventListName}
                </span>
              )}
              {log.status === 'removed' && <span className="ms-2 text-warning small fw-bold">(Removed)</span>}
              {log.visitCount > 1 && (
                <span 
                  className="ms-2 badge rounded-pill bg-secondary" 
                  style={{ fontSize: '0.65rem', cursor: 'pointer' }} 
                  title={`Entered ${log.visitCount} times today`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedLog({
                      ...log,
                      entryType: log.entryType || 'Member',
                      status: log.status || 'active',
                      isExpired: log.isExpired || false,
                      isFlagged: log.isFlagged || false,
                      hasIncident: log.hasIncident || false,
                      isBanned: log.isBanned || false
                    });
                    setShowDetailModal(true);
                  }}
                >
                  {log.visitCount} Visits
                </span>
              )}
            </div>
          </div>

          <div className="qr-card-time-wrap text-end">
            <div className="qr-time-display fw-bold">
              {isBypassed && <div className="text-info small" style={{ fontSize: '0.65rem' }}>BYPASS</div>}
              {formatScanTimeOnly(lastScanTime)}
            </div>
          </div>
        </div>

        {showFooterBar && (
          <div 
            className="entry-footer-bar border-top border-secondary pt-2 mt-1 d-flex align-items-center justify-content-between gap-2 w-100" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex align-items-center gap-2">
              {!isMember && !hasViolation && log.status !== 'removed' && (
                <button 
                  className="btn btn-sm btn-outline-warning d-flex align-items-center py-1 px-2 shadow-sm" 
                  style={{ fontSize: '0.75rem' }} 
                  onClick={(e) => { e.stopPropagation(); handleNoShow(log.id); }}
                >
                  <FaUserSlash className="me-1" size={12} /> No Show
                </button>
              )}

              {!log.isVerified && log.status !== 'removed' && (!hasViolation || isBypassed) && (
                <button 
                  className="btn btn-sm btn-success text-white fw-bold d-flex align-items-center py-1 px-2 shadow-sm" 
                  style={{ fontSize: '0.75rem' }} 
                  onClick={(e) => { e.stopPropagation(); handleVerifyEntry(log); }}
                >
                  <FaCheckCircle className="me-1" size={12} /> Verify
                </button>
              )}

              {isMember && (!hasViolation || isBypassed) && (
                <button
                  className="btn btn-sm btn-info text-white fw-bold d-flex align-items-center py-1 px-2 shadow-sm"
                  style={{ fontSize: '0.75rem' }}
                  onClick={async (e) => {
                    e.stopPropagation();
                    let memberEmail = log.rawPayload;
                    if (memberEmail && !memberEmail.includes('@')) {
                      const pQuery = query(collection(db, "profiles"), where("name", "==", log.fullName), limit(1));
                      const pSnap = await getDocs(pQuery);
                      if (!pSnap.empty) memberEmail = pSnap.docs[0].data().email;
                    }
                    if (memberEmail) {
                      setLastMemberEmail(memberEmail.toLowerCase());
                      setLastMemberName(log.fullName);
                      await fetchGuestsForMember(memberEmail);
                      setShowGuestModal(true);
                    } else {
                      toast.error("Could not link guest profile: Missing member email anchor.");
                    }
                  }}
                >
                  <FaUserPlus className="me-1" size={12} /> Guests
                </button>
              )}
            </div>

            <div className="d-flex align-items-center gap-2 ms-auto">
              {!isMember ? (
                <button 
                  className="btn btn-sm btn-outline-danger rounded-circle d-inline-flex align-items-center justify-content-center p-0" 
                  style={{ width: '28px', height: '28px' }} 
                  onClick={(e) => { e.stopPropagation(); handleOpenBanModal(log); }}
                >
                  <FaBan size={13} />
                </button>
              ) : (
                <div className="incident-report-btn-wrapper" onClick={(e) => e.stopPropagation()}>
                  <ReportIncident emailAddress={log.rawPayload || ''} theme="dark" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default LogCardItem;